// Adaptive quality: measures how fast this device actually renders the scene
// and moves along one quality LADDER — down immediately when it's too slow,
// back up quickly when there's headroom, but never back up to a level that
// already proved too slow in this scene (until a long calm stretch lets it
// try again). That stops the swing "highest → turn to the whole scene →
// lowest → highest again": after the first drop it stays below the level
// that failed, so the next heavy view only costs a small step.
//
// Why measure instead of reading hardware: browsers hide most of it
// (navigator.deviceMemory is Chromium-only and capped at 8 GB, Safari
// reports "Apple GPU", core counts say nothing about speed), and real cost
// depends on the model, screen size, browser and thermal state anyway.
//
// The ladder. Levers come in priority order from the caller (SceneHandler):
//  splats — streamed detail (−10%, to 50%) / static splat share (−5%, to a
//  floor) · spherical harmonics 3 → 2 → 1 → off · resolution (−5%, to 55%) ·
//  navigation quality (−10%, to 70%).
// The rungs (index > 0; index 0 = every lever at its default) take ONE step
// from each lever in turn — splats, SH 2, resolution −5%, navigation −10%,
// splats, SH 1, resolution −5%, …, SH off, … — so everything goes down (and
// back up) gradually together instead of one setting bottoming out before
// the next moves. A lever that has reached its floor drops out of the
// rotation. SH is two levers on one degree (3→1, then 1→off — `after`), so
// SH off comes in the round after SH 1: it saves a lot of GPU for little
// visible loss. Nothing ever goes ABOVE the defaults: spare GPU headroom is
// kept as frame rate (smoothness), not spent on extra detail — the app is
// GPU-bound, and pushing detail up until the fps fell to the raise gate
// made fast devices hover at ~30 fps instead of their refresh rate.
//
//  - Lowering (fps below LOW_FPS, or stutter — see STUTTER_*): down one rung.
//  - Ceiling: the rung where it had to lower proved too heavy here — raising
//    stops one rung below it.
//  - Raising (average over UP_HOLD_MS at least HIGH_FPS): up one rung, never
//    past the ceiling.
//  - Relaxing: after RELAX_START_MS without a drop the ceiling rises one rung
//    (one more try); each drop within that wait doubles it, up to RELAX_MAX_MS.
//    It can rise back to the highest rung that ever had to lower — reaching
//    that level again — but never above it, and never to the very top rung:
//    once slowness was detected, the second-highest rung is the most it
//    offers again this visit.
// Nothing is remembered between visits.
//
// A manual change to a lever (its Quality-menu control, or a URL pick)
// takes it off the ladder until its Auto is switched back on. ?adaptive=0
// disables the whole thing.

// Default gates (SceneHandler passes the same values, o.lowFps / o.highFps,
// for every device).
const LOW_FPS = 20;            // below this → lower (was 25 until 2026-10-28)
const HIGH_FPS = 30;           // at or above this (averaged over UP_HOLD_MS) → raise (was 35 until 2026-10-28)
const SAMPLE_MS = 500;         // rolling window for the "too slow" check
const CHECK_MS = 250;          // how often the windows are evaluated
const SETTLE_MS = 400;         // frames right after a change aren't counted
const SETTLE_AFTER_RAISE_MS = 1200; // a raised streamed budget streams harder for a moment
const UP_HOLD_MS = 1000;       // raise once the average fps over this long reaches HIGH_FPS
const WARMUP_MS = 1000;        // after the loading screen closes
const HITCH_MS = 250;          // single-frame hitches (GC, tab switch) don't count toward the average
// Stutter: the average can look fine (~28 fps) while long frames keep
// coming — on an iPad's WebGPU the frames handed over flow at a steady rate
// while the picture visibly stalls. STUTTER_FRAMES frames longer than
// STUTTER_FRAME_MS within STUTTER_WINDOW_MS count as too slow too (and block
// raising), whatever the average says.
const STUTTER_FRAME_MS = 60;
const STUTTER_FRAMES = 4;
const STUTTER_WINDOW_MS = 3000;
const RELAX_START_MS = 30000;  // calm this long → the ceiling may rise one rung
const RELAX_MAX_MS = 300000;   // the wait doubles per repeated drop, up to this

// Settings → Quality → Adaptive quality (per browser, on by default). Off:
// nothing adapts, every lever back to its default, and SceneHandler stops
// its per-frame measuring (as ?adaptive=0).
export const ADAPTIVE_KEY = 'adaptiveQuality';
export function isAdaptiveOn() {
  try { return localStorage.getItem(ADAPTIVE_KEY) !== '0'; } catch (e) { return true; }
}
// Old versions remembered levels per model (localStorage) — clear them.
try {
  ['cinesplat-adaptive-quality-v3', 'cinesplat-adaptive-quality-v4', 'cinesplat-adaptive-quality-v5']
    .forEach((k) => localStorage.removeItem(k));
} catch (e) { /* storage off */ }

// Rung values for a lever, from `from` toward `to` (excluding `from`,
// including `to`).
function rungValues(step, from, to) {
  const out = [];
  if (from === to) return out;
  const down = to < from;
  let v = from;
  for (let guard = 0; guard < 100; guard++) {
    if (step === 'scale') v = down ? Math.round(v * 0.85) : Math.round(v * 1.15); // 15%
    else if (step === 'scale5') v = down ? Math.round(v * 0.95) : Math.round(v * 1.05); // budget / splat share: 5%
    else if (step === 'points') v = down ? v - 8 : v + 8;                         // render quality slider
    else if (step === 'tens') v = down ? v - 10 : v + 10;                         // navigation quality %
    else if (step === 'fives') v = down ? v - 5 : v + 5;                          // resolution %
    else v = down ? v - 1 : v + 1;                                                // SH degree
    if (down ? v <= to : v >= to) { out.push(to); break; }
    out.push(v);
  }
  return out;
}

/**
 * @param {object} o
 * @param {string} o.modelId
 * @param {Array<object>} o.levers  in priority order; each:
 *   { name, label, step: 'scale' | 'scale5' | 'points' | 'degree' | 'tens' | 'fives', get(), set(v),
 *     min, max, base, adapt, fmt(v), settleAfterRaise? }
 * @param {number} [o.lowFps]   lower below this fps (default LOW_FPS)
 * @param {number} [o.highFps]  raise at or above this fps (default HIGH_FPS)
 * @param {() => boolean} o.isReady  true once the initial load has finished
 */
export function createAdaptiveQuality(o) {
  const lowFps = o.lowFps ?? LOW_FPS;
  const highFps = o.highFps ?? HIGH_FPS;
  const levers = o.levers.filter(Boolean).map((l) => ({ ...l, applied: null }));
  const byName = Object.fromEntries(levers.map((l) => [l.name, l]));
  // 'primary' = the first lever (streamed 'detail' / static 'splats'):
  // freezePrimary looked up a lever literally named 'primary', found none,
  // and a manual budget / splat share never stopped detail adapting.
  if (levers[0]) byName.primary = levers[0];
  let below = [];   // rungs below the top: [{ lever, value }]
  let above = [];   // rungs above the top
  let idx = 0;      // current rung (0 = defaults, >0 lower, <0 higher)
  let ceiling = 0;  // highest rung allowed (smallest index)
  let failCap = null; // highest rung that ever had to lower — relaxing stops here
  let relaxMs = RELAX_START_MS;
  let lastDropAt = -Infinity;
  let lastRelaxAt = 0;
  let readyAt = 0;
  let lastChangeAt = performance.now();
  let settleMs = SETTLE_MS;
  let lastCheckAt = 0;
  let disabled = false; // Settings switch off (setEnabled)
  let samples = [];   // [time, dt] over SAMPLE_MS
  let stutters = [];  // times of frames over STUTTER_FRAME_MS, within STUTTER_WINDOW_MS
  let upSamples = []; // [time, dt] over UP_HOLD_MS
  let upSince = 0;
  let lastFps = 0;

  const adapting = () => levers.filter((l) => l.adapt);

  function buildLadder() {
    below = [];
    above = [];
    // Round-robin: one step per lever per round, priority order within a
    // round. A lever with `after: <name>` continues that lever's slot once
    // it has run out (SH 3→1, then 1→off), or takes its place if absent.
    const live = adapting();
    const steps = (l) => rungValues(l.step, l.base, l.min).map((value) => ({ lever: l, value }));
    const rotation = [];
    live.forEach((l) => {
      const host = l.after && rotation.find((x) => x.name === l.after);
      if (host) host.rungs.push(...steps(l));
      else rotation.push({ name: l.name, rungs: steps(l) });
    });
    const longest = Math.max(0, ...rotation.map((x) => x.rungs.length));
    for (let k = 0; k < longest; k++) {
      rotation.forEach((x) => { if (k < x.rungs.length) below.push(x.rungs[k]); });
    }
    // No rungs above the defaults (see the header).
  }

  // Every adapting lever's value at rung i.
  function stateAt(i) {
    const st = new Map(adapting().map((l) => [l, l.base]));
    if (i > 0) below.slice(0, i).forEach(({ lever, value }) => st.set(lever, value));
    if (i < 0) above.slice(0, -i).forEach(({ lever, value }) => st.set(lever, value));
    return st;
  }

  // The rung matching the levers' current values (after a lever joins or
  // leaves the ladder), or the default rung.
  function findIndex() {
    for (let i = -above.length; i <= below.length; i++) {
      if ([...stateAt(i)].every(([l, v]) => l.get() === v)) return i;
    }
    return 0;
  }

  function resetWindows() {
    samples = [];
    upSamples = [];
    upSince = 0;
    stutters = [];
  }

  function apply(i, reason) {
    const st = stateAt(i);
    // Last lever first: the two SH levers share one degree, and turning SH
    // back on (1→off lever) must happen before raising the degree (3→1).
    [...st].reverse().forEach(([l, v]) => { if (l.get() !== v) l.set(v); });
    levers.forEach((l) => { l.applied = l.get(); });
    idx = i;
    lastChangeAt = performance.now();
    resetWindows();
    publish();
    window.dispatchEvent(new CustomEvent('adaptiveQualityChange', { detail: window.__adaptiveQuality }));
    if (reason) console.info(`[adaptive quality] ${describe()} — ${reason}`);
  }

  // What Settings shows, in the menu's own wording; "(manual)" = off the ladder.
  function describe() {
    const seen = new Set();
    return levers
      .filter((l) => !seen.has(l.label) && seen.add(l.label))
      .map((l) => `${l.label} ${l.fmt(l.get())}${l.adapt ? '' : ' (manual)'}`)
      .join(' · ');
  }

  function publish() {
    window.__adaptiveQuality = {
      fps: +lastFps.toFixed(1),
      rung: idx, ceiling, failCap, rungs: { above: above.length, below: below.length },
      levers: Object.fromEntries(levers.map((l) => [l.name, { value: l.get(), adapt: l.adapt }])),
      enabled: !disabled && adapting().length > 0,
    };
  }

  function rebuild() {
    buildLadder();
    idx = findIndex();
    ceiling = Math.max(-above.length, Math.min(ceiling, idx));
    levers.forEach((l) => { l.applied = l.get(); });
    publish();
  }

  // A manual change since our last apply takes that lever off the ladder.
  function noticeManualChanges() {
    let changed = false;
    levers.forEach((l) => {
      if (l.adapt && l.applied != null && l.get() !== l.applied) {
        l.adapt = false;
        changed = true;
        console.info(`[adaptive quality] ${l.label} set manually — no longer adapting it`);
      }
    });
    if (changed) rebuild();
  }

  function fpsOf(arr) {
    const total = arr.reduce((t, x) => t + x[1], 0);
    return total > 0 ? (arr.length * 1000) / total : 0;
  }

  function lower(fps, now, why) {
    if (idx >= below.length) return; // already at the bottom
    // This rung proved too heavy here: raising stops one rung below it, and
    // relaxing never goes above the highest rung that ever failed.
    ceiling = Math.max(ceiling, idx + 1);
    failCap = failCap == null ? idx : Math.min(failCap, idx);
    // Another drop within the relax wait → wait longer before trying higher.
    if (now - lastDropAt < relaxMs) relaxMs = Math.min(RELAX_MAX_MS, relaxMs * 2);
    lastDropAt = now;
    apply(idx + 1, why || `${fps.toFixed(1)} fps, below ${lowFps}`);
  }

  function raise(fps) {
    const target = idx - 1;
    if (target < ceiling) return;
    const rung = target < 0 ? above[-target - 1] : below[target];
    apply(target, `${fps.toFixed(1)} fps, at least ${highFps}`);
    if (rung && rung.lever.settleAfterRaise) settleMs = SETTLE_AFTER_RAISE_MS;
  }

  // After a calm stretch (no drop for relaxMs), the ceiling rises one rung.
  function relaxCeiling(now) {
    // No drop yet: the top. After one: the highest failed rung, but never
    // above the second-highest rung.
    const limit = failCap == null ? -above.length : Math.max(failCap, -above.length + 1);
    if (ceiling <= limit) return;
    if (now - lastDropAt >= relaxMs && now - lastRelaxAt >= relaxMs) {
      ceiling -= 1;
      lastRelaxAt = now;
      publish();
    }
  }

  function onFrame(dt) {
    if (disabled || adapting().length === 0) return;
    const now = performance.now();
    if (!o.isReady()) { readyAt = 0; return; }
    if (!readyAt) readyAt = now;
    if (document.hidden || now - readyAt < WARMUP_MS || now - lastChangeAt < settleMs) {
      resetWindows();
      return;
    }
    settleMs = SETTLE_MS;
    if (dt > STUTTER_FRAME_MS && dt < 1000) stutters.push(now);
    while (stutters.length && now - stutters[0] > STUTTER_WINDOW_MS) stutters.shift();
    if (!(dt > 0) || dt > HITCH_MS) return;
    samples.push([now, dt]);
    while (samples.length && now - samples[0][0] > SAMPLE_MS) samples.shift();
    if (!upSince) upSince = now;
    upSamples.push([now, dt]);
    while (upSamples.length && now - upSamples[0][0] > UP_HOLD_MS) upSamples.shift();
    if (now - lastCheckAt < CHECK_MS) return;
    lastCheckAt = now;
    if (!samples.length || now - samples[0][0] < SAMPLE_MS * 0.8) return;

    noticeManualChanges();
    if (adapting().length === 0) return;
    relaxCeiling(now);
    lastFps = fpsOf(samples);
    publish();

    const stuttering = stutters.length >= STUTTER_FRAMES;
    if (lastFps < lowFps) {
      lower(lastFps, now);
    } else if (stuttering) {
      lower(lastFps, now, `${stutters.length} frames over ${STUTTER_FRAME_MS} ms in ${STUTTER_WINDOW_MS / 1000} s`);
    } else if (now - upSince >= UP_HOLD_MS) {
      const upFps = fpsOf(upSamples);
      if (upFps >= highFps) raise(upFps);
    }
  }

  /** Drawing paused (nothing changed on screen): forget the windows, so
   *  idle redraw bursts never mix with — or stand in for — real motion. */
  function idle() {
    resetWindows();
  }

  /** Ignore the next `ms` of frames (e.g. after a capture). */
  function holdOff(ms) {
    lastChangeAt = performance.now();
    settleMs = ms;
    resetWindows();
  }

  /** Quality menu: levers' Auto off — they leave the ladder. */
  function freeze(...names) {
    names.forEach((n) => { if (byName[n]) byName[n].adapt = false; });
    rebuild();
  }
  /** Quality menu: levers' Auto on — back to their defaults, on the ladder.
   * Returns the first lever's value. */
  function resume(...names) {
    const ls = names.map((n) => byName[n]).filter(Boolean);
    if (!ls.length) return null;
    ls.forEach((l) => { l.adapt = true; });
    // Defaults, last lever first (see apply). For the SH pair that's the
    // 1→off lever (SH on) before the 3→1 lever (degree up).
    const order = [...ls].sort((a, b) => levers.indexOf(b) - levers.indexOf(a));
    order.forEach((l) => { if (l.get() !== l.base) l.set(l.base); });
    rebuild();
    console.info(`[adaptive quality] ${describe()} — Auto switched on`);
    window.dispatchEvent(new CustomEvent('adaptiveQualityChange', { detail: window.__adaptiveQuality }));
    return ls[0].get();
  }

  /** Settings → Adaptive quality. Off: stop, and put every adapting lever
   * back to its default (last lever first, see apply); on: start over from
   * the defaults. */
  function setEnabled(on) {
    if (disabled === !on) return;
    disabled = !on;
    if (disabled) {
      [...adapting()].reverse().forEach((l) => { if (l.get() !== l.base) l.set(l.base); });
      rebuild();
      ceiling = -above.length;
      failCap = null;
    }
    resetWindows();
    publish();
    console.info(`[adaptive quality] ${on ? 'on' : 'off (Settings)'}`);
    window.dispatchEvent(new CustomEvent('adaptiveQualityChange', { detail: window.__adaptiveQuality }));
  }

  buildLadder();
  levers.forEach((l) => { l.applied = l.get(); });
  idx = findIndex();
  ceiling = -above.length;
  publish();

  return {
    onFrame, idle, freeze, resume, holdOff, setEnabled,
    // Names the Quality menu / SceneHandler hooks use.
    freezePrimary: () => freeze('primary'), resumePrimary: () => resume('primary'),
    freezeBudget: () => freeze('primary'), resumeBudget: () => resume('primary'),
    freezeQuality: () => freeze('quality'), resumeQuality: () => resume('quality'),
    // Both SH levers (3→1 and 1→off) drive the same degree.
    freezeSh: () => freeze('sh', 'shOff'),
    resumeSh: () => resume('sh', 'shOff'),
  };
}
